import { useEffect, useState } from "react";
import { http } from "../api";
import { CalendarDays, MapPin, Users } from "lucide-react";
import { Modal, PageHeader, AddButton, FormRow, EmptyState, PersonLink } from "../shared";

const empty = { title: "", description: "", starts_at: "", venue: "", ward: 7, organizer: "", budget: 0, capacity: 100, status: "Planned" };
const regEmpty = { name: "", mobile: "", ward: 7 };

export default function Events() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [regs, setRegs] = useState([]);
  const [regForm, setRegForm] = useState(regEmpty);

  const load = () => http.get("/events").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { await http.post("/events", { ...form, ward: Number(form.ward), budget: Number(form.budget), capacity: Number(form.capacity) }); setShow(false); setForm(empty); load(); }
    finally { setSaving(false); }
  };

  const openDetail = async (e) => {
    setDetail(e);
    const r = await http.get(`/events/${e.event_id}/registrations`);
    setRegs(r.data);
  };

  const register = async () => {
    if (!regForm.name) return;
    await http.post("/events/register", { ...regForm, event_id: detail.event_id, ward: Number(regForm.ward) });
    const r = await http.get(`/events/${detail.event_id}/registrations`);
    setRegs(r.data); setRegForm(regEmpty); load();
  };

  return (
    <>
      <PageHeader eyebrow="PROGRAMS & MEETINGS" title="Programs & events" subtitle={`${rows.length} events on schedule`}
        actions={<AddButton onClick={() => setShow(true)} label="Create event" testid="events-add-button" />} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={CalendarDays} title="No events" hint="Schedule a program or meeting." /> : (
          <div className="grid-cards">
            {rows.map((e, i) => (
              <button key={e.event_id} className="event-card" data-testid={`event-card-${i}`} onClick={() => openDetail(e)}>
                <div className="event-date-big"><CalendarDays size={20} /><b>{(e.starts_at || "").slice(0, 10)}</b><small>{(e.starts_at || "").slice(11, 16)}</small></div>
                <div>
                  <b>{e.title}</b>
                  <p className="hint">{e.description}</p>
                  <div className="e-meta">
                    <span><MapPin size={13} /> {e.venue}</span>
                    <span><Users size={13} /> {e.registrations || 0} / {e.capacity} RSVPs</span>
                    <span>Ward {e.ward}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title="Create event" onClose={() => setShow(false)} testid="event-modal">
          <FormRow label="Title"><input data-testid="form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
          <FormRow label="Description"><input data-testid="form-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <div className="form-grid">
            <FormRow label="Starts at"><input data-testid="form-starts-input" type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} /></FormRow>
            <FormRow label="Venue"><input data-testid="form-venue-input" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></FormRow>
            <FormRow label="Organizer"><input data-testid="form-organizer-input" value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} /></FormRow>
            <FormRow label="Capacity"><input data-testid="form-capacity-input" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></FormRow>
            <FormRow label="Budget (₹)"><input data-testid="form-budget-input" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></FormRow>
            <FormRow label="Status"><select data-testid="form-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option>Planned</option><option>Live</option><option>Completed</option><option>Cancelled</option></select></FormRow>
          </div>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : "Create"}</button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={detail.title} onClose={() => setDetail(null)} testid="event-detail-modal">
          <p className="hint">{detail.description}</p>
          <div className="hh-meta">
            <span><CalendarDays size={13} /> {detail.starts_at}</span>
            <span><MapPin size={13} /> {detail.venue}</span>
            <span className="chip blue">{detail.status}</span>
          </div>
          <h4 style={{ marginTop: 15, fontSize: 12 }}>REGISTRATIONS ({regs.length}/{detail.capacity})</h4>
          <div className="reg-list">
            {regs.map((r, i) => (
              <div key={r.registration_id} className="reg-row" data-testid={`event-reg-${i}`}>
                <PersonLink contactId={r.contact_id} name={r.name} testid={`event-reg-link-${i}`}><b>{r.name}</b></PersonLink> <small>· {r.mobile} · Ward {r.ward}</small>
              </div>
            ))}
            {!regs.length && <p className="hint">No RSVPs yet.</p>}
          </div>
          <div className="reg-form">
            <input placeholder="Name" data-testid="reg-name-input" value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
            <input placeholder="Mobile" data-testid="reg-mobile-input" value={regForm.mobile} onChange={(e) => setRegForm({ ...regForm, mobile: e.target.value })} />
            <button className="primary" data-testid="reg-submit-button" onClick={register}>RSVP</button>
          </div>
        </Modal>
      )}
    </>
  );
}
